// CIN-13775: api, payment and auth pick the validator message language from Accept-Language
// (en -> English, anything else -> German). Browsers send their own Accept-Language, which can
// disagree with the UI language, so the shared client sends the active UI locale explicitly on writes
// (GETs are left alone, see fetch-helper.ts).
//
// Client-side the locale is the <html lang> next-intl renders. Server-side there is no document, so
// each app registers its request-locale getter (next-intl's `getLocale`) once at startup via
// setLocaleResolver(). Stored on globalThis for the same reason as the client identity (CIN-14379).
const RESOLVER_KEY = Symbol.for('cineamo.api/locale-resolver');

type LocaleResolver = () => Promise<string | undefined> | string | undefined;
type ResolverGlobal = { [RESOLVER_KEY]?: LocaleResolver };

export function setLocaleResolver(fn: LocaleResolver): void {
    (globalThis as ResolverGlobal)[RESOLVER_KEY] = fn;
}

/** UI locale (`de`, `en-GB`, `de_AT`, ...) -> the two languages the backend knows. */
export function toApiLanguage(locale?: string | null): 'de' | 'en' {
    return /^en([-_]|$)/i.test(locale ?? '') ? 'en' : 'de';
}

/** `{ 'Accept-Language': 'de' | 'en' }`, or `{}` when no locale is known (e.g. outside a request). */
export async function getAcceptLanguageHeader(): Promise<Record<string, string>> {
    let locale: string | undefined;
    try {
        locale =
            typeof document !== 'undefined'
                ? document.documentElement.lang
                : await (globalThis as ResolverGlobal)[RESOLVER_KEY]?.();
    } catch {
        // getLocale() throws outside a request scope — send no header rather than fail the call
    }
    return locale ? { 'Accept-Language': toApiLanguage(locale) } : {};
}
